import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Film, Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import EmptyState from '@/components/ui/EmptyState';
import Spinner from '@/components/ui/Spinner';
import InFlightCard from '@/components/dashboard/InFlightCard';
import ProductionRow from '@/components/dashboard/ProductionRow';
import SectionHead from '@/components/dashboard/SectionHead';
import { useDashboard } from '@/hooks/useDashboard';

const HISTORY_PREVIEW = 8;

/**
 * The landing page: what's running right now (and what's blocked on you), then the
 * run history. The full project list lives in the left rail, so this stays focused
 * on productions rather than duplicating it.
 */
export default function Projects() {
  const navigate = useNavigate();
  const { t } = useTranslation(['dashboard', 'common']);
  const { hasProjects, active, past, reviews, typeById, loading, hasMoreHistory, loadMoreHistory, loadingMore } = useDashboard();
  const [showAll, setShowAll] = useState(false);

  const shown = showAll ? past : past.slice(0, HISTORY_PREVIEW);

  return (
    <div className="mx-auto max-w-5xl px-8 py-7">
      <div
        className="flex items-start justify-between gap-5 rounded-xl border border-border
                   bg-gradient-to-br from-violet/10 to-primary/[0.04] px-7 py-6"
      >
        <div>
          <h2 className="m-0 font-display text-[22px] font-semibold">{t('hero.title')}</h2>
          <p className="mt-2 max-w-xl text-[13.5px] leading-relaxed text-muted-foreground">{t('hero.body')}</p>
        </div>
        <Button className="shrink-0" onClick={() => navigate('/projects/new')}>
          <Plus className="mr-2 h-4 w-4" />
          {t('hero.newProject')}
        </Button>
      </div>

      {active.length > 0 && (
        <>
          <SectionHead title={t('section.inFlight')} count={active.length} live />
          <div className="space-y-2.5">
            {active.map((p) => (
              <InFlightCard
                key={p.production_id}
                production={p}
                needsReview={reviews[p.production_id]}
                type={typeById[p.production_id]}
              />
            ))}
          </div>
        </>
      )}

      {past.length > 0 && (
        <>
          <SectionHead title={t('section.recent')} count={past.length} />
          <div className="space-y-2.5">
            {shown.map((p) => (
              <ProductionRow key={p.id} production={p} />
            ))}
          </div>
          <div className="mt-3 flex items-center gap-4">
            {past.length > HISTORY_PREVIEW && (
              <button
                className="text-xs text-muted-foreground underline-offset-2 hover:underline"
                onClick={() => setShowAll((v) => !v)}
              >
                {showAll ? t('action.showFewer', { ns: 'common' }) : t('action.showAll', { ns: 'common', count: past.length })}
              </button>
            )}
            {/* History is paged: once everything loaded is shown, offer the next page. */}
            {(showAll || past.length <= HISTORY_PREVIEW) && hasMoreHistory && (
              <button
                className="inline-flex items-center gap-1.5 text-xs text-muted-foreground underline-offset-2 hover:underline disabled:opacity-60"
                onClick={loadMoreHistory}
                disabled={loadingMore}
              >
                {loadingMore && <Spinner />}
                {t('action.loadMore', { ns: 'common' })}
              </button>
            )}
          </div>
        </>
      )}

      {loading && (
        <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
          <Spinner /> {t('empty.loading')}
        </div>
      )}

      {!loading && !hasProjects && (
        <div className="mt-7">
          <EmptyState
            title={t('empty.noProjectsTitle')}
            action={
              <Button onClick={() => navigate('/projects/new')}>
                <Plus className="mr-2 h-4 w-4" />
                {t('empty.createFirst')}
              </Button>
            }
          >
            {t('empty.noProjects')}
          </EmptyState>
        </div>
      )}

      {!loading && hasProjects && active.length === 0 && past.length === 0 && (
        <div className="mt-7">
          <EmptyState title={t('empty.nothingProducedTitle')}>
            <span className="inline-flex items-center gap-1.5">
              <Film className="h-3.5 w-3.5" />
              {t('empty.nothingProduced')}
            </span>
          </EmptyState>
        </div>
      )}
    </div>
  );
}
